import { useState } from 'react';
import type { FileItem } from '../lib/types';
import { Thumbnail } from './Thumbnail';
import { ShareDialog } from './ShareDialog';
import { FilePreviewModal } from './FilePreviewModal';
import { Button, EmptyState } from './ui';
import { formatBytes, formatDate, fileKind } from '../lib/format';
import { mintMediaUrl, deleteFile, ApiError } from '../lib/api';

const PREVIEWABLE = new Set(['image', 'video', 'audio', 'pdf']);

export function FileList({ files, onChanged }: { files: FileItem[]; onChanged: () => void }) {
  const [shareFor, setShareFor] = useState<FileItem | null>(null);
  const [previewFor, setPreviewFor] = useState<FileItem | null>(null);
  const [error, setError] = useState('');

  async function download(file: FileItem) {
    try {
      const url = await mintMediaUrl(file.id, 'download');
      window.location.href = url;
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Download failed');
    }
  }

  async function remove(file: FileItem) {
    if (!window.confirm(`Delete “${file.name}”? This cannot be undone.`)) return;
    try {
      await deleteFile(file.id);
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Delete failed');
    }
  }

  if (files.length === 0) {
    return <EmptyState title="No files here yet" hint="Upload files to see them listed here." />;
  }

  return (
    <>
      {error && <p className="mb-2 text-sm text-red-600">{error}</p>}
      <div className="divide-y divide-slate-100 overflow-hidden rounded-lg border border-slate-200 bg-white">
        {files.map((file) => (
          <div key={file.id} className="flex items-center gap-3 px-3 py-2">
            <Thumbnail file={file} />
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium">{file.name}</p>
              <p className="text-xs text-slate-500">
                {formatBytes(file.size_bytes)} · {formatDate(file.created_at)}
              </p>
            </div>
            <div className="flex items-center gap-1">
              {PREVIEWABLE.has(fileKind(file.mime_type)) && (
                <Button variant="ghost" onClick={() => setPreviewFor(file)}>
                  Preview
                </Button>
              )}
              <Button variant="ghost" onClick={() => download(file)}>
                Download
              </Button>
              <Button variant="ghost" onClick={() => setShareFor(file)}>
                Share
              </Button>
              <Button variant="ghost" onClick={() => remove(file)} aria-label="Delete">
                🗑
              </Button>
            </div>
          </div>
        ))}
      </div>
      {shareFor && <ShareDialog file={shareFor} onClose={() => setShareFor(null)} />}
      {previewFor && <FilePreviewModal file={previewFor} onClose={() => setPreviewFor(null)} />}
    </>
  );
}
